/**
 * MIRROR von `src/lib/bv-positions-katalog.ts` (Backend).
 *
 * Backend ist Source of Truth. Bei Backend-Änderungen 1:1 hierher übernehmen.
 * Drift-Schutz: `bv-lib-drift.test.ts`.
 *
 * BV-Plan §5.2.
 */

import type { BvModulId, Frequenz } from './bv-modul-templates';
import { formatFrequenz } from './bv-modul-templates';

export interface KatalogPosition {
  id: string;
  beschreibung: string;
  frequenz: Frequenz;
  defaultAktiv: boolean;
  bedingung?: 'aufzug';
}

const HAUSTECHNIK: KatalogPosition[] = [
  {
    id: 'ht-1',
    beschreibung: 'Kontrolle der technischen und elektrischen Einrichtungen',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
  },
  {
    id: 'ht-2',
    beschreibung: 'Kontrolle des Aufzugs',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
    bedingung: 'aufzug',
  },
  {
    id: 'ht-3',
    beschreibung: 'Überwachung der Ordnung in den Gemeinschaftsbereichen',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
  },
  {
    id: 'ht-4',
    beschreibung: 'Sichtprüfung der Heizungsanlage (Überprüfung der Heizkörper und Heizungsregler)',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
  },
  {
    id: 'ht-5',
    beschreibung: 'Austausch defekter Leuchtmittel (Material wird gesondert abgerechnet)',
    frequenz: { count: 1, period: 'asNeeded' },
    defaultAktiv: true,
  },
  {
    id: 'ht-6',
    beschreibung:
      'Meldung von anstehenden Reparaturen/Wartungen zum Jahresende (z.B. Allgemeinzähler, Heizungswartung)',
    frequenz: { count: 1, period: 'yearly' },
    defaultAktiv: true,
  },
  {
    id: 'ht-7',
    beschreibung: 'Zählerstände ablesen und an die Hausverwaltung übermitteln',
    frequenz: { count: 1, period: 'yearly' },
    defaultAktiv: true,
  },
];

const INNENREINIGUNG: KatalogPosition[] = [
  {
    id: 'ir-1',
    beschreibung: 'Kehren und Wischen der Treppenhäuser sowie der Gemeinschaftsflure',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-2',
    beschreibung: 'Reinigung der Haustüren / Entfernen von Griffspuren',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-3',
    beschreibung: 'Handlauf feucht abwischen',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-4',
    beschreibung: 'Fensterbänke sauber halten',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-5',
    beschreibung: 'Reinigung der Heizkörper, Brüstungen und Lichtschalter',
    frequenz: { count: 1, period: 'monthly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-6',
    beschreibung: 'Reinigung der Briefkästen und der Klingelanlage',
    frequenz: { count: 1, period: 'monthly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-7',
    beschreibung: 'Treppenhausgeländer und Fußleisten sauber halten',
    frequenz: { count: 1, period: 'monthly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-8',
    beschreibung: 'Kehren und, sofern möglich, feuchtes Wischen der Kellergänge',
    frequenz: { count: 1, period: 'monthly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-9',
    beschreibung: 'Kehren der Technikräume, Fahrradabstellräume und Waschküchen',
    frequenz: { count: 1, period: 'yearly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-10',
    beschreibung: 'Feuchte Reinigung der gemeinschaftlichen Keller- und Zwischentüren',
    frequenz: { count: 1, period: 'yearly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-11',
    beschreibung: 'Reinigung der Lampenschirme im Treppenhaus von außen',
    frequenz: { count: 1, period: 'yearly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-12',
    beschreibung:
      'Bedarfsmäßige Reinigung der Treppenhausfenster (sofern ohne Hilfsmittel erreichbar)',
    frequenz: { count: 4, period: 'yearly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-13',
    beschreibung: 'Fegen des Müllraums bzw. der Müllräume, bei Bedarf Wischen',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
  },
  {
    id: 'ir-14',
    beschreibung: 'Äußere Fahrstuhltüren inkl. Rahmen reinigen',
    frequenz: { count: 1, period: 'monthly' },
    defaultAktiv: true,
    bedingung: 'aufzug',
  },
  {
    id: 'ir-15',
    beschreibung: 'Fahrstuhlkabinen und Führungsschienen reinigen',
    frequenz: { count: 1, period: 'monthly' },
    defaultAktiv: true,
    bedingung: 'aufzug',
  },
];

const AUSSENREINIGUNG: KatalogPosition[] = [
  {
    id: 'ar-1',
    beschreibung: 'Befestigte Flächen von Unrat befreien',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
  },
  {
    id: 'ar-2',
    beschreibung: 'Kehren der Fußwege und öffentlichen Gehwege',
    frequenz: { count: 2, period: 'monthly' },
    defaultAktiv: true,
  },
  {
    id: 'ar-3',
    beschreibung: 'Reinigung des Müllplatzes (Entfernung von Müll und Unrat)',
    frequenz: { count: 1, period: 'weekly' },
    defaultAktiv: true,
  },
  {
    id: 'ar-4',
    beschreibung: 'Laubbeseitigung auf befestigten Wegen im Herbst (Entsorgung vor Ort)',
    frequenz: { count: 1, period: 'asNeeded' },
    defaultAktiv: true,
  },
  {
    id: 'ar-5',
    beschreibung:
      'Reinigung von Abläufen (ACO-Rinnen), Gullys (Verstopfungen beseitigen) und Kellerlichtschächten',
    frequenz: { count: 1, period: 'yearly' },
    defaultAktiv: true,
  },
  {
    id: 'ar-6',
    beschreibung: 'Schneebeseitigung auf Außenflächen (z.B. Gehwege, Zufahrten) und Nachstreuen',
    frequenz: { count: 1, period: 'asNeeded' },
    defaultAktiv: true,
  },
  {
    id: 'ar-7',
    beschreibung: 'Beseitigung von Unkraut und Moos auf befestigten Flächen',
    frequenz: { count: 1, period: 'yearly' },
    defaultAktiv: true,
  },
];

const GRUENFLAECHENPFLEGE: KatalogPosition[] = [
  {
    id: 'gf-1',
    beschreibung: 'Rasen mähen (Wuchshöhe)',
    frequenz: { count: 2, period: 'monthly' },
    defaultAktiv: true,
  },
  {
    id: 'gf-2',
    beschreibung: 'Unkrautbeseitigung in Beeten, Hecken und Sträuchern',
    frequenz: { count: 1, period: 'monthly' },
    defaultAktiv: true,
  },
  {
    id: 'gf-3',
    beschreibung:
      'Formschnitt/leichter Rückschnitt von Hecken, Sträuchern, Büschen und Bäumen (bis 4,5 m Höhe)',
    frequenz: { count: 2, period: 'yearly' },
    defaultAktiv: true,
  },
  {
    id: 'gf-4',
    beschreibung: 'Beseitigung von Laub in der Gartenanlage',
    frequenz: { count: 2, period: 'yearly' },
    defaultAktiv: true,
  },
  {
    id: 'gf-5',
    beschreibung: 'Verkehrsflächen sauber halten',
    frequenz: { count: 1, period: 'ongoing' },
    defaultAktiv: true,
  },
];

const WINTERDIENST: KatalogPosition[] = [
  {
    id: 'wd-1',
    beschreibung:
      'Räumung und Schneebeseitigung auf allen notwendigen Flächen (z.B. Gehwege, Zufahrten, Eingänge)',
    frequenz: { count: 1, period: 'agreed' },
    defaultAktiv: true,
  },
  {
    id: 'wd-2',
    beschreibung:
      'Streuen der Verkehrsflächen mit abstumpfenden Mitteln (Einsatz von geeignetem Streumaterial)',
    frequenz: { count: 1, period: 'agreed' },
    defaultAktiv: true,
  },
  {
    id: 'wd-3',
    beschreibung: 'Entfernung des Streugutes im Rahmen der Außenreinigung (im Frühjahr)',
    frequenz: { count: 1, period: 'agreed' },
    defaultAktiv: true,
  },
];

export const POSITIONEN_KATALOG: Record<BvModulId, KatalogPosition[]> = {
  haustechnik: HAUSTECHNIK,
  innenreinigung: INNENREINIGUNG,
  aussenreinigung: AUSSENREINIGUNG,
  gruenflaechenpflege: GRUENFLAECHENPFLEGE,
  winterdienst: WINTERDIENST,
  bereitschaftsdienst: [],
  sonderarbeiten: [],
};

export function positionAlsTextzeile(p: KatalogPosition): string {
  return `${p.beschreibung} — ${formatFrequenz(p.frequenz)}`;
}
